// SPDX-License-Identifier: MIT
// Copyright (c) 2022-2023 The Pybricks Authors

import { Icon } from '@blueprintjs/core';
import {
    Archive,
    Home,
    InfoSign,
    KeyEnter,
    People,
    Play,
    Plus,
} from '@blueprintjs/icons';
import React, { useCallback, useMemo, useState } from 'react';
import Joyride, {
    ACTIONS,
    CallBackProps,
    EVENTS,
    Locale,
    STATUS,
    Step,
    Styles,
} from 'react-joyride';
import { useDispatch } from 'react-redux';
import { useEffectOnce, useLocalStorage, useTernaryDarkMode } from 'usehooks-ts';
import { Activity, useActivitiesSelectedActivity } from '../activities/hooks';
import { legoRegisteredTrademark, pybricksBlue } from '../app/constants';
import { useSelector } from '../reducers';
import { tourStart, tourStop } from './actions';
import { useI18n } from './i18n';

const WelcomeStep = React.memo(function WelcomeStep() {
    const i18n = useI18n();

    return (
        <>
            <p>
                {i18n.translate('steps.welcome.message', {
                    appName: 'PyBricks CaptiClient',
                })}
            </p>
            <p>
                {i18n.translate('steps.welcome.action', {
                    next: <strong>{i18n.translate('next')}</strong>,
                })}
            </p>
        </>
    );
});

const CaptiClientInfoStep = React.memo(function CaptiClientInfoStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.captiClientInfo.message', {
                icon: (
                    <Icon icon={<InfoSign />} style={{ verticalAlign: 'text-top' }} />
                ),
            })}
        </p>
    );
});

const HomeStep = React.memo(function HomeStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.home.message', {
                icon: <Icon icon={<Home />} style={{ verticalAlign: 'text-top' }} />,
            })}
        </p>
    );
});

const CoopStep = React.memo(function CoopStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.coop.message', {
                icon: <Icon icon={<People />} style={{ verticalAlign: 'text-top' }} />,
            })}
        </p>
    );
});

const JerryAiStep = React.memo(function JerryAiStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.jerryAi.message', {
                icon: (
                    <Icon icon={<KeyEnter />} style={{ verticalAlign: 'text-top' }} />
                ),
            })}
        </p>
    );
});

const AddFileStep = React.memo(function AddFileStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.newFile.message', {
                icon: <Icon icon={<Plus />} style={{ verticalAlign: 'text-top' }} />,
            })}
        </p>
    );
});

const BackupFilesStep = React.memo(function BackupFilesStep() {
    const i18n = useI18n();

    return (
        <>
            <p>{i18n.translate('steps.backupFiles.message')}</p>
            <p>
                {i18n.translate('steps.backupFiles.action', {
                    icon: (
                        <Icon
                            icon={<Archive />}
                            style={{ verticalAlign: 'text-top' }}
                        />
                    ),
                })}
            </p>
        </>
    );
});

const FlashPybricksFirmwareStep = React.memo(function FlashPybricksFirmwareStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.flashPybricksFirmware.message', {
                appName: 'PyBricks CaptiClient',
            })}
        </p>
    );
});

const RestoreOfficialFirmwareStep = React.memo(function RestoreOfficialFirmwareStep() {
    const i18n = useI18n();

    return (
        <p>
            {i18n.translate('steps.restoreOfficialFirmware.message', {
                lego: legoRegisteredTrademark,
            })}
        </p>
    );
});

const ConnectToHubStep = React.memo(function ConnectToHubStep() {
    const i18n = useI18n();

    return <p>{i18n.translate('steps.connectToHub.message')}</p>;
});

const DownloadAndRunStep = React.memo(function DownloadAndRunStep() {
    const i18n = useI18n();

    return (
        <>
            <p>
                {i18n.translate('steps.downloadAndRun.message', {
                    icon: (
                        <Icon icon={<Play />} style={{ verticalAlign: 'text-top' }} />
                    ),
                })}
            </p>
            <p>{i18n.translate('steps.downloadAndRun.runAgain')}</p>
        </>
    );
});

const Tour: React.FunctionComponent = () => {
    const [selectedActivity, setSelectedActivity] = useActivitiesSelectedActivity();
    const [showOnStartup, setShowOnStartup] = useLocalStorage(
        'tour.showOnStartup',
        true,
    );
    const [stepIndex, setStepIndex] = useState(0);
    const { isRunning } = useSelector((s) => s.tour);
    const { isDarkMode } = useTernaryDarkMode();
    const dispatch = useDispatch();
    const i18n = useI18n();

    const steps = useMemo<Step[]>(
        () => [
            {
                target:
                    selectedActivity === Activity.Settings
                        ? '#pb-settings-tour-button'
                        : '[itemId=pb-activities-settings-tab]',
                content: <WelcomeStep />,
                disableBeacon: selectedActivity === Activity.Settings,
            },
            {
                target: '#pb-toolbar-capticlient-button',
                content: <CaptiClientInfoStep />,
                disableBeacon: true,
            },
            {
                target:
                    selectedActivity === Activity.Home
                        ? '#pb-home-new-file-button'
                        : '[itemId=pb-activities-home-tab]',
                content: <HomeStep />,
                disableBeacon: selectedActivity === Activity.Home,
            },
            {
                target:
                    selectedActivity === Activity.Home
                        ? '#pb-home-coop-panel'
                        : '[itemId=pb-activities-home-tab]',
                content: <CoopStep />,
                disableBeacon: selectedActivity === Activity.Home,
            },
            {
                target: '#pb-toolbar-ai-button',
                content: <JerryAiStep />,
                disableBeacon: true,
            },
            {
                target:
                    selectedActivity === Activity.Settings
                        ? '#pb-settings-flash-pybricks-button'
                        : '[itemId=pb-activities-settings-tab]',
                content: <FlashPybricksFirmwareStep />,
                disableBeacon: selectedActivity === Activity.Settings,
            },
            {
                target:
                    selectedActivity === Activity.Settings
                        ? '#pb-settings-flash-official-button'
                        : '[itemId=pb-activities-settings-tab]',
                content: <RestoreOfficialFirmwareStep />,
                disableBeacon: selectedActivity === Activity.Settings,
            },
            {
                target:
                    selectedActivity === Activity.Explorer
                        ? '#pb-explorer-add-button'
                        : '[itemId=pb-activities-explorer-tab]',
                content: <AddFileStep />,
                disableBeacon: selectedActivity === Activity.Explorer,
            },
            {
                target:
                    selectedActivity === Activity.Explorer
                        ? '#pb-explorer-archive-button'
                        : '[itemId=pb-activities-explorer-tab]',
                content: <BackupFilesStep />,
                disableBeacon: selectedActivity === Activity.Explorer,
            },
            {
                target: '#pb-toolbar-bluetooth-button',
                content: <ConnectToHubStep />,
                disableBeacon: true,
            },
            {
                target: '#pb-toolbar-run-button',
                content: <DownloadAndRunStep />,
                disableBeacon: true,
            },
        ],
        [selectedActivity],
    );

    const styles = useMemo<Partial<Styles>>(
        () => ({
            options: {
                primaryColor: pybricksBlue,
                textColor: isDarkMode ? '#eef8f4' : '#1f1f1f',
                backgroundColor: isDarkMode ? '#172523' : '#ffffff',
                arrowColor: isDarkMode ? '#172523' : '#ffffff',
                overlayColor: 'rgba(31, 31, 31, 0.28)',
                spotlightShadow: '0 0 0 9999px rgba(31, 31, 31, 0.28)',
                zIndex: 2000,
            },
            tooltip: {
                borderRadius: 20,
                boxShadow: isDarkMode
                    ? '0 8px 32px rgba(0, 0, 0, 0.36)'
                    : '0 8px 32px rgba(51, 142, 127, 0.18)',
                padding: 22,
            },
            tooltipTitle: {
                color: pybricksBlue,
                fontWeight: 900,
            },
            buttonNext: {
                backgroundColor: pybricksBlue,
                borderRadius: 999,
                color: '#ffffff',
                fontWeight: 800,
                padding: '10px 18px',
            },
            buttonBack: {
                color: isDarkMode ? '#acebc9' : pybricksBlue,
                fontWeight: 800,
                marginRight: 8,
            },
            buttonClose: {
                color: isDarkMode ? '#acebc9' : pybricksBlue,
            },
            buttonSkip: {
                color: isDarkMode ? '#b2c7c1' : '#5f6368',
                fontWeight: 800,
            },
        }),
        [isDarkMode],
    );

    const locale = useMemo<Locale>(
        () => ({
            back: i18n.translate('back'),
            close: i18n.translate('close'),
            last: i18n.translate('last'),
            next: i18n.translate('next'),
            open: i18n.translate('open'),
            skip: i18n.translate('skip'),
        }),
        [i18n],
    );

    const callback = useCallback(
        (event: CallBackProps) => {
            if (event.action === ACTIONS.CLOSE || event.status === STATUS.FINISHED) {
                dispatch(tourStop());
                setStepIndex(0);
                return;
            }

            if (event.type === EVENTS.STEP_AFTER) {
                const nextIndex = stepIndex + (event.action === ACTIONS.PREV ? -1 : 1);
                const nextStep = steps.at(nextIndex);

                // Some components may not be mounted when the next/back button
                // is pressed. If this is the case, first target the tab, then
                // request to activate that tab. When the tab panel is mounted,
                // the target will automatically update.

                if (typeof nextStep?.target === 'string') {
                    if (nextStep.target.startsWith('[itemId=pb-activities-explorer-')) {
                        setSelectedActivity(Activity.Explorer);
                    } else if (
                        nextStep.target.startsWith('[itemId=pb-activities-home-')
                    ) {
                        setSelectedActivity(Activity.Home);
                    } else if (
                        nextStep.target.startsWith('[itemId=pb-activities-settings-')
                    ) {
                        setSelectedActivity(Activity.Settings);
                    }
                }

                setStepIndex(nextIndex);
            }
        },
        [dispatch, setSelectedActivity, stepIndex, setStepIndex, steps],
    );

    // automatically show the tour on the first run only
    useEffectOnce(() => {
        if (showOnStartup) {
            setSelectedActivity(Activity.Settings);
            dispatch(tourStart());
            setShowOnStartup(false);
        }
    });

    return (
        <Joyride
            run={isRunning}
            stepIndex={stepIndex}
            continuous={true}
            showProgress={true}
            steps={steps}
            styles={styles}
            locale={locale}
            callback={callback}
        />
    );
};

export default Tour;
